import { useMemo, useState, useCallback } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { AnimatePresence } from "framer-motion";

import "./Reel.scss";
import "./Adaptations.scss";

import Overlay from "../../../layouts/overlay/Overlay";
import Controls from "./controls/Controls";
import Frame from "../frame/Frame";
import ImagesLoader from "./images-loader/ImagesLoader";
import Loading from "./loading/Loading";
// import GuideButton from "../menu/icon-button/GuideButton";

export default function Reel({
    folder,
    amount,
    extension = ".jpg",
    qualitySteps,
    defaultControlsPart = 0,
    stops,
    overlay_elements,
    wrapper_animation_key,
    startFrame,
    onFullLoad = () => console.log("Reel succesfully loaded."),
    onReelReady = () => {},
}) {
    const defaultIndex = useMemo(
        () => stops.find((stop) => stop.default).index,
        [stops]
    );
    const [currentIndex, setCurrentIndex] = useState(defaultIndex);

    const [images, setImages] = useState([]);
    const [loadedPart, setLoadedPart] = useState(0);
    const [loadingPhase, setLoadingPhase] = useState(0); // 0 = not started, 1-3 = loading phases
    const [reelReady, setReelReady] = useState(false);
    const loadingPercents = useMemo(() => loadedPart * 100, [loadedPart]);

    // Rotation step based on loading phase (coarser steps for earlier phases)
    const rotationStep = useMemo(() => {
        // Phase 1 → step 4, Phase 2 → step 2, Phase 3+ → step 1
        if (loadingPhase <= 1) return 4;
        if (loadingPhase === 2) return 2;
        return 1;
    }, [loadingPhase]);

    // Interval timing based on loading phase (slower for coarser steps to maintain same visual speed)
    const rotationInterval = useMemo(() => {
        // Base interval adjusted for step size
        // Phase 1 (step 4): 4x slower = 80ms
        // Phase 2 (step 2): 2x slower = 40ms
        // Phase 3 (step 1): normal = 20ms
        if (loadingPhase <= 1) return 80;
        if (loadingPhase === 2) return 40;
        return 20;
    }, [loadingPhase]);

    // Handler when a loading phase completes
    const handlePhaseComplete = useCallback((phase) => {
        setLoadingPhase(phase);
        // Enable reel interaction after phase 1 completes
        if (phase >= 1) {
            setReelReady(true);
            onReelReady();
        }
    }, [onReelReady]);

    // Handler when all images are fully loaded
    const handleFullLoad = useCallback(() => {
        setReelReady(true);
        setLoadingPhase(3);
        onFullLoad();
    }, [onFullLoad]);

    // Get image source for current frame
    const getCurrentImageSrc = useCallback(() => {
        if (loadingPercents < 25 || !images.length) {
            return startFrame;
        }

        const img = images[currentIndex - 1];
        if (img) {
            return img.src;
        }

        // If the exact frame isn't loaded yet, find the nearest loaded frame
        // This handles phases where not all frames are loaded
        const step = rotationStep;
        const nearestIndex = Math.round((currentIndex - 1) / step) * step;
        const nearestImg = images[nearestIndex];
        if (nearestImg) {
            return nearestImg.src;
        }

        // Fallback to start frame
        return startFrame;
    }, [images, currentIndex, loadingPercents, startFrame, rotationStep]);

    function getStopIndexes(stops) {
        let indexes = [];
        for (let stop of stops) {
            indexes.push(stop.index);
        }
        return indexes;
    }
    const stop_indexes = useMemo(() => getStopIndexes(stops), [stops]);

    function checkIfStop(index) {
        return stop_indexes.includes(index);
    }

    function nextStop(direction) {
        // Use rotation step based on loading phase
        const step = rotationStep;

        const intervalStop = setInterval(() => {
            setCurrentIndex((state) => {
                let newState;
                if (state >= amount && direction > 0) {
                    newState = 1;
                } else if (state <= 1 && direction < 0) {
                    newState = amount;
                } else {
                    newState = state + direction * step;
                    // Ensure we don't go out of bounds
                    if (newState > amount) newState = amount;
                    if (newState < 1) newState = 1;
                }

                // Check if we've crossed a stop (step might skip over it)
                // Look for any stop between previous state and new state
                if (direction > 0) {
                    for (let i = state + 1; i <= newState; i++) {
                        if (checkIfStop(i)) {
                            newState = i;
                            break;
                        }
                    }
                } else {
                    for (let i = state - 1; i >= newState; i--) {
                        if (checkIfStop(i)) {
                            newState = i;
                            break;
                        }
                    }
                }

                if (checkIfStop(newState)) {
                    clearInterval(intervalStop);
                }

                return newState;
            });
        }, rotationInterval); // Adjust interval based on loading phase for consistent visual speed
    }

    function findClosestStop(index) {
        let stops = [];
        for (let stop_index of stop_indexes) {
            stops.push({
                index: stop_index,
                difference: stop_index - index,
                inverted_difference: amount + stop_index - index,
            });
        }

        function getMinStepsAmountAndDirection(
            difference,
            inverted_difference
        ) {
            let difference_abs = Math.abs(difference);
            let inverted_difference_abs = Math.abs(inverted_difference);

            function getDirection(number) {
                if (number > 0) {
                    return 1;
                } else if (number < 0) {
                    return -1;
                } else {
                    return 0;
                }
            }

            if (difference_abs <= inverted_difference_abs) {
                return [difference_abs, getDirection(difference)];
            } else {
                return [
                    inverted_difference_abs,
                    getDirection(inverted_difference),
                ];
            }
        }

        for (let stop of stops) {
            let [min_steps, direction] = getMinStepsAmountAndDirection(
                stop.difference,
                stop.inverted_difference
            );

            let score = 0;
            for (let stop_check of stops) {
                if (
                    min_steps <= Math.abs(stop_check.difference) &&
                    min_steps <= Math.abs(stop_check.inverted_difference)
                ) {
                    score += 1;
                }
            }

            if (score === stops.length) {
                stop.direction = direction;
                return stop;
            }
        }
    }

    function goToClosestStop(index) {
        let closestStop = findClosestStop(currentIndex);
        nextStop(closestStop.direction);
    }

    let startX = 0;
    const [isGrabbing, setIsGrabbing] = useState(false);

    const handleMouseDown = (e) => {
        startX = e.clientX;
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        setIsGrabbing(true);
    };

    const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);

        setIsGrabbing(false);
    };

    let deltaXRest = 0;
    let neededDistance = 1200 / amount;

    const handleMouseMove = (e) => {
        if (
            (e.clientX - startX < 0 && deltaXRest > 0) ||
            (e.clientX - startX > 0 && deltaXRest < 0)
        ) {
            deltaXRest = 0;
        }

        const deltaX = e.clientX - startX + deltaXRest;
        const moveAmount = Math.trunc(deltaX / neededDistance);

        if (moveAmount !== 0) {
            deltaXRest = 0;

            setCurrentIndex((state) => {
                let newState;
                newState = state - moveAmount;
                if (newState > amount) {
                    newState = newState - amount;
                } else if (newState <= 0) {
                    newState = newState + amount;
                }
                return newState;
            });
        }

        startX = e.clientX;
        deltaXRest = deltaXRest + (deltaX % neededDistance);
    };

    return (
        <div
            className="reel"
            style={
                {
                    // filter: `blur(${100 - loadingPercents * qualitySteps}px)`,
                    // backgroundImage: `url("../../../${folder}/${qualitySteps}/${defaultIndex}${extension}")`,
                }
            }
        >
            <ImagesLoader
                images={images}
                setImages={setImages}
                folder={folder}
                amount={amount}
                extension={extension}
                qualitySteps={qualitySteps}
                checkIfHighByDefault={checkIfStop}
                setLoadedPart={setLoadedPart}
                onFullLoad={handleFullLoad}
                onPhaseComplete={handlePhaseComplete}
            />
            <AnimatePresence mode="wait">
                {!reelReady && (
                    <Loading
                        percents={
                            loadingPercents * 4 > 100
                                ? 100
                                : loadingPercents * 4
                        }
                    />
                )}
            </AnimatePresence>
            <div
                className="reel-frame"
                style={{ cursor: reelReady ? (isGrabbing ? "grabbing" : "grab") : "default" }}
                {...(reelReady && {
                    onMouseDown: handleMouseDown,
                    onMouseUp: () => goToClosestStop(currentIndex),
                })}
            >
                <Overlay>
                    <AnimatePresence mode="wait">
                        {reelReady && (
                            <>
                                {overlay_elements}
                                <div className="reel-menu">
                                    <Controls
                                        defaultIndex={defaultIndex}
                                        defaultPart={defaultControlsPart}
                                        currentIndex={currentIndex}
                                        amount={amount}
                                        nextStop={nextStop}
                                    />
                                </div>
                            </>
                        )}
                    </AnimatePresence>
                </Overlay>
                <Frame
                    image={getCurrentImageSrc()}
                    wrapper={
                        !isGrabbing && reelReady
                            ? stops.find((stop) => stop.index === currentIndex)
                                  ?.wrapper
                            : null
                    }
                    wrapper_animation_key={wrapper_animation_key}
                />
            </div>
        </div>
    );
}
